import _typeof from '@babel/runtime/helpers/typeof';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _PropTypes from 'prop-types';
import Downshift from 'downshift';
import * as React from 'react';
import styled from 'styled-components';
import { KEYS } from '../../constants/keys.js';
import { useThemeContext } from '../../styles/themes/themes.js';
import { getAdornment } from '../../utils/adornment.js';
import { Box } from '../Box/Box.js';
import { IconSearch } from '../Icons/IconSearch.js';
import { InputContainer } from '../Input/InputContainer.js';
import { InputInput } from '../Input/InputInput.js';
import { ListItem } from '../ListItem/ListItem.js';
import { Popper } from '../Popper/Popper.js';
import { FetchDropdownItems } from './FetchDropdownItems.js';
import { Spinner } from '../Spinner/Spinner.js';
import { Tag } from '../Tag/Tag.js';
import { jsx, jsxs } from 'react/jsx-runtime';

var _excluded = ["aria-label", "aria-labelledby", "items", "fetchResults", "onChange", "value", "placeholder", "popperProps", "startAdornment", "style"],
    _excluded2 = ["className", "endAdornment", "success"],
    _excluded3 = ["triggerRef"];
var DropdownItemContainer = styled(Box).withConfig({
  displayName: "Search__DropdownItemContainer",
  componentId: "sc-18fs9zi-0"
})(["&&{margin-top:4px;", ";}"], function (_ref) {
  var noBorder = _ref.noBorder;
  return noBorder && " border-width: 0;";
});
var MenuWrapper = styled.div.withConfig({
  displayName: "Search__MenuWrapper",
  componentId: "sc-18fs9zi-1"
})(["&&{width:320px;max-height:300px;overflow-y:auto;}"]);
var StyledTag = styled(Tag).withConfig({
  displayName: "Search__StyledTag",
  componentId: "sc-18fs9zi-2"
})(["&&{margin:0 5px 3px 0;}"]);
var StyledInputContainer = styled(InputContainer).withConfig({
  displayName: "Search__StyledInputContainer",
  componentId: "sc-18fs9zi-3"
})(["&&{min-height:32px;width:320px;max-height:120px;overflow-y:auto;height:auto;", ";}"], function (_ref2) {
  var shouldWrap = _ref2.shouldWrap;
  return shouldWrap && "flex-wrap: wrap; padding-bottom: 2px;";
});

var Search = function Search(props) {
  var _useThemeContext = useThemeContext(),
      i18n = _useThemeContext.i18n;

  var defaultPlaceholder = i18n.Search.defaultPlaceholder;

  var ariaLabel = props["aria-label"],
      ariaLabelledBy = props["aria-labelledby"],
      _props$items = props.items,
      items = _props$items === void 0 ? [] : _props$items,
      fetchResults = props.fetchResults,
      onChange = props.onChange,
      _props$value = props.value,
      value = _props$value === void 0 ? [] : _props$value,
      _props$placeholder = props.placeholder,
      placeholder = _props$placeholder === void 0 ? defaultPlaceholder : _props$placeholder,
      _props$popperProps = props.popperProps,
      popperProps = _props$popperProps === void 0 ? {} : _props$popperProps,
      _props$startAdornment = props.startAdornment,
      startAdornment = _props$startAdornment === void 0 ? /*#__PURE__*/jsx(IconSearch, {
    size: 16
  }) : _props$startAdornment,
      _props$style = props.style,
      inputComponentStyles = _props$style === void 0 ? {} : _props$style,
      otherInputProps = _objectWithoutProperties(props, _excluded);

  var className = otherInputProps.className,
      endAdornment = otherInputProps.endAdornment,
      success = otherInputProps.success,
      inputInputProps = _objectWithoutProperties(otherInputProps, _excluded2);

  var inputContainerProps = {
    className: className,
    disabled: inputInputProps.disabled,
    endAdornment: endAdornment,
    error: inputInputProps.error,
    success: success
  };
  var fetchedItemsLength = React.useRef(0);
  var inputContainerRef = React.useRef(null);
  var prevSelectedValues = React.useRef(value);
  var downshiftRef = React.useRef({});
  var inputRef = React.useRef(null);

  var handleSelect = function handleSelect(selectedValue) {
    if (onChange) {
      prevSelectedValues.current = value;
      onChange([].concat(_toConsumableArray(value), [selectedValue]));
    }
  };

  var removeSelectedValue = function removeSelectedValue(val) {
    if (onChange) {
      prevSelectedValues.current = value;
      onChange(value.filter(function (selectedVal) {
        return selectedVal.value !== val.value;
      }));
    }
  };

  var handleInputKeyDown = function handleInputKeyDown(_ref3) {
    var event = _ref3.event,
        inputValue = _ref3.inputValue;

    if (event.key === KEYS.BACKSPACE && value.length && !inputValue) {
      removeSelectedValue(value[value.length - 1]);
    }
  };

  var handleInputBlur = function handleInputBlur() {
    var _inputContainerRef$cu;

    (_inputContainerRef$cu = inputContainerRef.current) === null || _inputContainerRef$cu === void 0 ? void 0 : _inputContainerRef$cu.scrollTo(0, 0);
  };

  React.useEffect(function () {
    if (value.length > prevSelectedValues.current.length) {
      var _downshiftRef$current;

      (_downshiftRef$current = downshiftRef.current) === null || _downshiftRef$current === void 0 ? void 0 : _downshiftRef$current.reset();
    }
  }, [value]);

  var itemToString = function itemToString(i) {
    return i ? i.children : "";
  };

  return /*#__PURE__*/jsx(Downshift, {
    onSelect: handleSelect,
    itemToString: itemToString,
    selectedItem: null,
    defaultHighlightedIndex: 0,
    ref: downshiftRef,
    children: function children(_ref4) {
      var getInputProps = _ref4.getInputProps,
          getItemProps = _ref4.getItemProps,
          getMenuProps = _ref4.getMenuProps,
          isOpen = _ref4.isOpen,
          inputValue = _ref4.inputValue,
          highlightedIndex = _ref4.highlightedIndex,
          clearItems = _ref4.clearItems,
          setHighlightedIndex = _ref4.setHighlightedIndex;

      var DropdownItemContainerComponent = function DropdownItemContainerComponent(_ref5) {
        var loading = _ref5.loading,
            dropdownItems = _ref5.dropdownItems;
        var filteredItems = dropdownItems.filter(function (item) {
          return value.findIndex(function (selectedVal) {
            return selectedVal.value === item.value;
          }) < 0 && (!inputValue || item.children.toLowerCase().includes(inputValue.toLowerCase()));
        });
        fetchedItemsLength.current = filteredItems.length;
        return /*#__PURE__*/jsxs(DropdownItemContainer, {
          elevation: 1,
          borderRadius: 1,
          noBorder: filteredItems.length === 0 && !loading,
          children: [loading && /*#__PURE__*/jsx("div", {
            style: {
              margin: 8
            },
            children: /*#__PURE__*/jsx(Spinner, {})
          }), /*#__PURE__*/jsx(MenuWrapper, _objectSpread(_objectSpread({}, getMenuProps({
            "aria-labelledby": ariaLabelledBy
          })), {}, {
            children: filteredItems.map(function (item, index) {
              return /*#__PURE__*/jsxs(ListItem, _objectSpread(_objectSpread({}, getItemProps({
                key: item.value,
                index: index,
                item: item
              })), {}, {
                active: highlightedIndex === index,
                children: [item.startAdornment && /*#__PURE__*/jsx("div", {
                  style: {
                    marginRight: 8
                  },
                  children: getAdornment(item.startAdornment)
                }), item.children]
              }), index);
            })
          }))]
        });
      };

      DropdownItemContainerComponent.displayName = "DropdownItemContainerComponent";
      var dropdownContainer = null;

      if (isOpen) {
        if (fetchResults) {
          dropdownContainer = /*#__PURE__*/jsx(FetchDropdownItems, {
            fetchResults: fetchResults,
            inputText: inputValue,
            onLoaded: function onLoaded() {
              clearItems();
              setHighlightedIndex(0);
            },
            render: DropdownItemContainerComponent
          });
        } else if (items.length) {
          dropdownContainer = DropdownItemContainerComponent({
            dropdownItems: items
          });
        }
      } else {
        dropdownContainer = null;
      }

      return /*#__PURE__*/jsx("div", {
        children: /*#__PURE__*/jsx(Popper, _objectSpread(_objectSpread({}, popperProps), {}, {
          showing: isOpen,
          renderTrigger: function renderTrigger(triggerProps) {
            return /*#__PURE__*/jsx("div", _objectSpread(_objectSpread({}, triggerProps), {}, {
              children: /*#__PURE__*/jsxs(StyledInputContainer, _objectSpread(_objectSpread({}, inputContainerProps), {}, {
                ref: inputContainerRef,
                focusRef: inputRef,
                shouldWrap: !!value.length,
                style: inputComponentStyles,
                startAdornment: value.length ? void 0 : startAdornment,
                children: [value.map(function (val, index) {
                  return /*#__PURE__*/jsx(StyledTag, {
                    value: itemToString(val),
                    startAdornment: val.startAdornment,
                    onRemove: function onRemove() {
                      return removeSelectedValue(val);
                    },
                    disabled: inputInputProps.disabled
                  }, index);
                }), /*#__PURE__*/jsx(InputInput, _objectSpread(_objectSpread(_objectSpread({}, inputInputProps), getInputProps(_objectSpread({
                  onKeyDown: function onKeyDown(event) {
                    handleInputKeyDown({
                      event: event,
                      inputValue: inputValue
                    });
                  },
                  "aria-label": ariaLabel,
                  "aria-labelledby": ariaLabelledBy
                }, fetchedItemsLength.current < 1 ? {
                  "aria-activedescendant": void 0
                } : {}))), {}, {
                  as: "input",
                  ref: inputRef,
                  placeholder: value.length ? "" : placeholder,
                  autosize: true,
                  onBlur: handleInputBlur
                }))]
              }))
            }));
          },
          renderContent: function renderContent(_ref6) {
            _ref6.triggerRef;
                var rest = _objectWithoutProperties(_ref6, _excluded3);

            return /*#__PURE__*/jsx("div", _objectSpread(_objectSpread({}, rest), {}, {
              children: dropdownContainer
            }));
          }
        }))
      });
    }
  });
};

Search.propTypes = {
  "aria-label": _PropTypes.string,
  "aria-labelledby": _PropTypes.string,
  "className": _PropTypes.string,
  "disabled": _PropTypes.bool,
  "endAdornment": function checkAdornment(props, propName, componentName) {
    var isReactNodeType = function isReactNodeType(el) {
      var REACT_ELEMENT_TYPE = typeof Symbol === 'function' && Symbol.for ? Symbol.for('react.element') : 0xeac7;
      var isValidElement = _typeof(el) === 'object' && el !== null && el['\$\$typeof'] === REACT_ELEMENT_TYPE;
      return el === undefined || el === null || typeof el === 'boolean' || typeof el === 'string' || isValidElement;
    };

    var adornment = (propName === 'startAdornment' || propName === 'endAdornment') && props[propName];

    if (!isReactNodeType(adornment)) {
      var name = adornment.displayName || adornment.name || 'Component';

      if (!(name.indexOf('Icon') === 0)) {
        return new Error("Invalid prop \"" + propName + "\". Invalid React component with name \"" + name + "\" was supplied to \"" + componentName + "\". Only Icon components are allowed.\n");
      }
    }
  },
  "error": _PropTypes.bool,
  "fetchResults": _PropTypes.func,
  "items": _PropTypes.arrayOf(_PropTypes.shape({
    "children": _PropTypes.string.isRequired,
    "startAdornment": function checkAdornment(props, propName, componentName) {
      var isReactNodeType = function isReactNodeType(el) {
        var REACT_ELEMENT_TYPE = typeof Symbol === 'function' && Symbol.for ? Symbol.for('react.element') : 0xeac7;
        var isValidElement = _typeof(el) === 'object' && el !== null && el['\$\$typeof'] === REACT_ELEMENT_TYPE;
        return el === undefined || el === null || typeof el === 'boolean' || typeof el === 'string' || isValidElement;
      };

      var adornment = (propName === 'startAdornment' || propName === 'endAdornment') && props[propName];

      if (!isReactNodeType(adornment)) {
        var name = adornment.displayName || adornment.name || 'Component';

        if (!(name.indexOf('Icon') === 0)) {
          return new Error("Invalid prop \"" + propName + "\". Invalid React component with name \"" + name + "\" was supplied to \"" + componentName + "\". Only Icon components are allowed.\n");
        }
      }
    },
    "value": _PropTypes.string.isRequired
  })),
  "onChange": _PropTypes.func,
  "placeholder": _PropTypes.string,
  "popperProps": _PropTypes.shape({
    "containerSelector": _PropTypes.string,
    "zIndex": _PropTypes.number
  }),
  "startAdornment": function checkAdornment(props, propName, componentName) {
    var isReactNodeType = function isReactNodeType(el) {
      var REACT_ELEMENT_TYPE = typeof Symbol === 'function' && Symbol.for ? Symbol.for('react.element') : 0xeac7;
      var isValidElement = _typeof(el) === 'object' && el !== null && el['\$\$typeof'] === REACT_ELEMENT_TYPE;
      return el === undefined || el === null || typeof el === 'boolean' || typeof el === 'string' || isValidElement;
    };

    var adornment = (propName === 'startAdornment' || propName === 'endAdornment') && props[propName];

    if (!isReactNodeType(adornment)) {
      var name = adornment.displayName || adornment.name || 'Component';

      if (!(name.indexOf('Icon') === 0)) {
        return new Error("Invalid prop \"" + propName + "\". Invalid React component with name \"" + name + "\" was supplied to \"" + componentName + "\". Only Icon components are allowed.\n");
      }
    }
  },
  "style": _PropTypes.object,
  "success": _PropTypes.bool,
  "value": _PropTypes.arrayOf(_PropTypes.shape({
    "children": _PropTypes.string.isRequired,
    "startAdornment": function checkAdornment(props, propName, componentName) {
      var isReactNodeType = function isReactNodeType(el) {
        var REACT_ELEMENT_TYPE = typeof Symbol === 'function' && Symbol.for ? Symbol.for('react.element') : 0xeac7;
        var isValidElement = _typeof(el) === 'object' && el !== null && el['\$\$typeof'] === REACT_ELEMENT_TYPE;
        return el === undefined || el === null || typeof el === 'boolean' || typeof el === 'string' || isValidElement;
      };

      var adornment = (propName === 'startAdornment' || propName === 'endAdornment') && props[propName];

      if (!isReactNodeType(adornment)) {
        var name = adornment.displayName || adornment.name || 'Component';

        if (!(name.indexOf('Icon') === 0)) {
          return new Error("Invalid prop \"" + propName + "\". Invalid React component with name \"" + name + "\" was supplied to \"" + componentName + "\". Only Icon components are allowed.\n");
        }
      }
    },
    "value": _PropTypes.string.isRequired
  }))
};
Search.displayName = "Search";

export { Search };
